import { Lock } from 'lucide-react';
import { SupplyVehicleSuppliersPanel, type SupplierContributionRow } from './SupplyVehicleSuppliersPanel';
import type { SupplyAgentLogisticsCalculateResponse, SupplyAgentOverCapacityOption } from '@/services/supplyAgentService';
import { MOTION_STYLES, useCountUp, useGrowIn } from './visualMotion';

interface SupplyFillToTargetPanelProps {
  proposal: SupplyAgentLogisticsCalculateResponse;
  accepted: Record<number, boolean>;
  onToggle: (productId: number, accepted: boolean) => void;
  onApply: () => void;
  onDiscard: () => void;
  /** Cambia entre reducir y completar cuando el pedido excede el vehículo. */
  onSelectOption?: (option: SupplyAgentOverCapacityOption) => void;
  switchingOption?: boolean;
  /** Líneas bloqueadas que el ajuste no ha tocado. */
  lockedCount?: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function numeric(value: unknown): number | null {
  const parsed = Number(value);
  return value != null && Number.isFinite(parsed) ? parsed : null;
}

function formatNumber(value: number | null, maximumFractionDigits = 1): string {
  return value == null ? '—' : value.toLocaleString(undefined, { maximumFractionDigits });
}

function sumKnown(values: Array<number | null>): number | null {
  const known = values.filter((value): value is number => value != null);
  return known.length > 0 ? known.reduce((total, value) => total + value, 0) : null;
}

function formatMoney(value: number | null): string {
  return value == null ? '—' : `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} €`;
}

/**
 * Los avisos de «objetivo no alcanzable» traen el desglose tras «Motivos:» (o «Motivi:») separado por «; »:
 * se muestra un titular y un punto por motivo para poder leerlo de un vistazo.
 */
function splitNoteReasons(note: string): { head: string; reasons: string[] } {
  const match = /^(.*?)\.\s*(?:Motivos|Motivi):\s*(.+)$/s.exec(note);
  if (!match) return { head: note, reasons: [] };
  // Los «;» de dentro de un paréntesis no separan motivos.
  const reasons = match[2].split(/;\s+(?![^()]*\))/).map((reason) => reason.trim()).filter(Boolean);
  return { head: match[1], reasons };
}

function signed(value: number, format: (value: number) => string): string {
  return `${value < 0 ? '−' : '+'}${format(Math.abs(value))}`;
}

/** Una cifra del resumen antes/después: valor principal, variación y ayuda opcional. */
function SummaryStat({
  label,
  value,
  delta,
  title,
  tone,
  note,
}: {
  label: string;
  value: string;
  delta?: string | null;
  title?: string;
  tone?: 'good' | 'bad' | 'neutral';
  note?: string | null;
}) {
  const toneClass = tone === 'bad' ? 'text-rose-700' : tone === 'good' ? 'text-emerald-700' : 'text-slate-500';
  return (
    <div title={title} className="rounded-md border border-slate-100 bg-white px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{value}</p>
      {delta ? <p className={`text-[11px] font-medium tabular-nums ${toneClass}`}>{delta}</p> : null}
      {note ? <p className="text-[10px] text-amber-700">{note}</p> : null}
    </div>
  );
}

function fillPct(used: unknown, capacity: unknown): number | null {
  const usedValue = numeric(used);
  const capacityValue = numeric(capacity);
  return usedValue != null && capacityValue != null && capacityValue > 0 ? (usedValue / capacityValue) * 100 : null;
}

/** Barra antes → después: lo que ya había en azul y lo que añade la propuesta en verde, con la marca del objetivo. */
function BeforeAfterBar({
  label,
  before,
  after,
  target,
}: {
  label: string;
  before: number | null;
  after: number | null;
  target: number | null;
}) {
  const clamp = (value: number | null) => Math.max(0, Math.min(100, value ?? 0));
  const base = clamp(before);
  const total = Math.max(base, clamp(after));
  const grownTotal = useGrowIn(total);
  const grownBase = useGrowIn(base);
  const shownAfter = useCountUp(after);
  return (
    <div className="min-w-[180px] flex-1">
      <div className="mb-1 flex items-baseline justify-between gap-2 text-[11px]">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-500">
          {formatNumber(before)}% → <b className="text-sm text-emerald-800">{formatNumber(shownAfter)}%</b>
        </span>
      </div>
      <div className="relative h-2.5 rounded-full bg-white shadow-inner ring-1 ring-emerald-100">
        <div className="sv-fill absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${grownTotal}%` }} />
        <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-sky-400 to-sky-600 transition-[width] duration-700" style={{ width: `${grownBase}%` }} />
        {target != null ? <span className="absolute -top-0.5 h-3.5 w-0.5 rounded bg-rose-500" style={{ left: `${clamp(target)}%` }} title={`${formatNumber(target, 0)}%`} /> : null}
      </div>
    </div>
  );
}

/**
 * Propuesta de «Completar hasta objetivo»: ocupación antes/después y cambio total de unidades.
 * El detalle por línea (y aceptar o rechazar cada una) está en la columna «Ajuste objetivo» de la tabla.
 */
export function SupplyFillToTargetPanel({
  proposal,
  accepted,
  onToggle,
  onApply,
  onDiscard,
  onSelectOption,
  switchingOption = false,
  lockedCount = 0,
  t,
}: SupplyFillToTargetPanelProps) {
  const adjustments = proposal.line_adjustments || [];
  const container = proposal.container || {};
  const utilization = proposal.utilization || {};
  const overCapacity = proposal.over_capacity ?? null;
  // En envío LCL la capacidad es el volumen/peso contratado, no un vehículo.
  const isLcl = proposal.shipment_mode === 'lcl';
  const overOptions = (proposal.alternatives || []).filter((alternative) => alternative.kind);
  // Los porcentajes se calculan sobre los vehículos de la opción elegida (1 si el pedido cabe).
  const vehicles = Math.max(1, numeric(utilization.vehicles) ?? 1);
  const usableVolume = numeric(container.usable_volume_m3);
  const usableWeight = numeric(container.usable_payload_kg);
  const volumeBefore = fillPct(utilization.volume_m3_before, usableVolume != null ? usableVolume * vehicles : null);
  const volumeAfter = numeric(utilization.volume_fill_pct_after)
    ?? fillPct(utilization.volume_m3_after, usableVolume != null ? usableVolume * vehicles : null);
  const weightBefore = fillPct(utilization.weight_kg_before, usableWeight != null ? usableWeight * vehicles : null);
  const weightAfter = numeric(utilization.weight_fill_pct_after)
    ?? fillPct(utilization.weight_kg_after, usableWeight != null ? usableWeight * vehicles : null);
  const target = numeric(utilization.container_target_fill_pct);
  const acceptedAdjustments = adjustments.filter((adjustment) => accepted[adjustment.product_id]);
  const acceptedCount = acceptedAdjustments.length;
  const unitsBefore = acceptedAdjustments.reduce((total, adjustment) => total + (numeric(adjustment.old_units) ?? 0), 0);
  const unitsAfter = acceptedAdjustments.reduce((total, adjustment) => total + (numeric(adjustment.new_units) ?? 0), 0);
  const unitsDelta = unitsAfter - unitsBefore;
  const raisedCount = acceptedAdjustments.filter((adjustment) => (numeric(adjustment.new_units) ?? 0) > (numeric(adjustment.old_units) ?? 0)).length;
  const loweredCount = acceptedAdjustments.filter((adjustment) => (numeric(adjustment.new_units) ?? 0) < (numeric(adjustment.old_units) ?? 0)).length;
  const shownDelta = useCountUp(unitsDelta);
  // Resumen antes/después de las líneas aceptadas: se recalcula al marcar o desmarcar líneas.
  const summary = proposal.summary ?? null;
  const amountBefore = numeric(summary?.amount_before);
  const amountDelta = sumKnown(acceptedAdjustments.map((adjustment) => numeric(adjustment.amount_delta_euros)));
  const amountAfter = amountBefore != null ? amountBefore + (amountDelta ?? 0) : null;
  const demandTotal = numeric(summary?.daily_demand_total);
  const coveredBefore = numeric(summary?.covered_units_before);
  const coverageBefore = numeric(summary?.avg_coverage_days_before);
  const coveredDelta = acceptedAdjustments.reduce((total, adjustment) => total + (adjustment.has_demand ? numeric(adjustment.delta_units) ?? 0 : 0), 0);
  const coverageAfter = demandTotal && coveredBefore != null ? (coveredBefore + coveredDelta) / demandTotal : null;
  const overstockDelta = sumKnown(acceptedAdjustments.map((adjustment) => numeric(adjustment.overstock_amount_delta_euros)));
  const overstockUnitsDelta = sumKnown(acceptedAdjustments.map((adjustment) => numeric(adjustment.overstock_units_delta)));
  const unpricedCount = numeric(summary?.lines_without_price) ?? 0;
  const unpricedNote = unpricedCount > 0 ? t('supply.supplyAgent.fillToTargetSummaryUnpriced', { count: unpricedCount }) : null;
  // Objetivo económico (HU5): importe alcanzado con las líneas aceptadas y comparación ahorro / sobrestock.
  const economic = proposal.economic ?? null;
  const economicTarget = numeric(economic?.target_amount_euros);
  const economicReached = economicTarget != null && amountAfter != null && amountAfter >= economicTarget - 0.01;
  const economicMissing = economicTarget != null && amountAfter != null ? Math.max(0, economicTarget - amountAfter) : null;
  const economicSaving = numeric(economic?.saving_euros);
  const appliedSaving = economicSaving != null && economicReached ? economicSaving : null;
  const economicNet = appliedSaving != null && overstockDelta != null ? appliedSaving - overstockDelta : null;
  // Aportación de cada proveedor al vehículo compartido, con las líneas aceptadas (HU8).
  const supplierRows: SupplierContributionRow[] = (proposal.suppliers || []).length > 1
    ? (proposal.suppliers || []).map((row) => {
      const mine = acceptedAdjustments.filter((adjustment) => adjustment.supplier_id === row.supplier_id);
      const sumOf = (pick: (adjustment: (typeof mine)[number]) => number | null | undefined) => mine.reduce((total, adjustment) => total + (pick(adjustment) ?? 0), 0);
      const volumeCapacity = usableVolume != null ? usableVolume * vehicles : null;
      const weightCapacity = usableWeight != null ? usableWeight * vehicles : null;
      const amountAfter = row.amount_before + sumOf((adjustment) => numeric(adjustment.amount_delta_euros));
      return {
        supplierId: row.supplier_id,
        name: row.supplier_name || String(row.supplier_id ?? ''),
        units: row.units_before,
        unitsAfter: row.units_before + sumOf((adjustment) => numeric(adjustment.delta_units)),
        volumePct: volumeCapacity ? (row.volume_m3_before / volumeCapacity) * 100 : null,
        volumePctAfter: volumeCapacity ? ((row.volume_m3_before + sumOf((adjustment) => numeric(adjustment.volume_delta_m3))) / volumeCapacity) * 100 : null,
        weightPct: weightCapacity ? (row.weight_kg_before / weightCapacity) * 100 : null,
        weightPctAfter: weightCapacity ? ((row.weight_kg_before + sumOf((adjustment) => numeric(adjustment.weight_delta_kg))) / weightCapacity) * 100 : null,
        amount: row.amount_before,
        amountAfter,
        minAmount: row.min_amount ?? null,
        minMet: row.min_amount != null ? amountAfter >= row.min_amount - 0.01 : null,
      };
    })
    : [];
  // Pallets completos o bases completas (HU2): avance con las líneas aceptadas.
  const goal = proposal.goal ?? null;
  const palletsBefore = numeric(goal?.pallets_before);
  const palletsAfter = palletsBefore != null
    ? palletsBefore + acceptedAdjustments.reduce((total, adjustment) => total + ((numeric(adjustment.pallet_eq_after) ?? 0) - (numeric(adjustment.pallet_eq_before) ?? 0)), 0)
    : null;
  const palletTarget = numeric(goal?.target_pallets);
  const palletMissing = palletTarget != null && palletsAfter != null ? Math.max(0, palletTarget - palletsAfter) : null;
  const palletReached = palletMissing != null && palletMissing <= 1e-6;
  const layersBefore = numeric(goal?.lines_open_before) ?? 0;
  const layersOpen = Math.max(0, layersBefore - acceptedAdjustments.length);
  // Topes de la empresa aplicados: presupuesto y stock máximo.
  const limits = proposal.limits ?? null;
  const limitItems = limits
    ? [
      limits.budget_total != null ? t('supply.supplyAgent.fillToTargetLimitBudget', { amount: formatMoney(limits.budget_total) }) : null,
      Object.keys(limits.budget_by_supplier || {}).length > 0 ? t('supply.supplyAgent.fillToTargetLimitBudgetSupplier') : null,
      limits.stock_capped_products > 0 ? t('supply.supplyAgent.fillToTargetLimitStock', { count: limits.stock_capped_products }) : null,
      (limits.shelf_life_products ?? 0) > 0 ? t('supply.supplyAgent.fillToTargetLimitShelfLife', { count: limits.shelf_life_products }) : null,
    ].filter((item): item is string => item != null)
    : [];
  const unitsLabel = (value: number) => `${formatNumber(value, 0)} ${t('supply.supplyAgent.fillToTargetOptionUnits')}`;
  const notes = [proposal.detail, ...(proposal.warnings || [])]
    .filter((note, index, all): note is string => Boolean(note) && all.indexOf(note) === index)
    .slice(0, 5);

  return (
    <section
      aria-label={t('supply.supplyAgent.fillToTargetTitle')}
      className="sv-rise mb-3 rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-3 shadow-sm"
    >
      <style>{MOTION_STYLES}</style>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">{t(economic ? 'supply.supplyAgent.fillToTargetTitleAmount' : goal?.objective === 'pallet' ? 'supply.supplyAgent.fillToTargetTitlePallet' : goal?.objective === 'layer' ? 'supply.supplyAgent.fillToTargetTitleLayer' : overCapacity ? (isLcl ? 'supply.supplyAgent.fillToTargetTitleOverLcl' : 'supply.supplyAgent.fillToTargetTitleOver') : 'supply.supplyAgent.fillToTargetTitle')}</h3>
          {goal?.objective === 'pallet' && palletTarget != null ? (
            <p className="mt-0.5 text-[11px] text-slate-600">{t('supply.supplyAgent.fillToTargetPalletTarget', { pallets: formatNumber(palletTarget, 2) })}</p>
          ) : null}
          {goal?.objective === 'layer' ? (
            <p className="mt-0.5 text-[11px] text-slate-600">{t('supply.supplyAgent.fillToTargetLayerTarget')}</p>
          ) : null}
          {economicTarget != null ? (
            <p className="mt-0.5 text-[11px] text-slate-600">
              {t(economic?.target_amount_source === 'supplier_rule' ? 'supply.supplyAgent.fillToTargetAmountTargetSupplier' : 'supply.supplyAgent.fillToTargetAmountTarget', { amount: formatMoney(economicTarget) })}
            </p>
          ) : null}
          {target != null ? (
            <p className="mt-0.5 text-[11px] text-slate-600">{t('supply.supplyAgent.fillToTargetTarget', { target: formatNumber(target, 0) })}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={onDiscard} className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
            {t('supply.supplyAgent.fillToTargetDiscard')}
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={acceptedCount === 0}
            className="rounded-md bg-emerald-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('supply.supplyAgent.fillToTargetApply', { count: acceptedCount })}
          </button>
        </div>
      </div>
      {overCapacity && overOptions.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-slate-700">
            {t(isLcl ? 'supply.supplyAgent.fillToTargetOverTitleLcl' : 'supply.supplyAgent.fillToTargetOverTitle', { vehicles: formatNumber(numeric(overCapacity.vehicles_exact), 2) })}
          </p>
          <div role="radiogroup" aria-label={t('supply.supplyAgent.fillToTargetOverGroup')} className="mt-2 grid gap-2 sm:grid-cols-2">
            {overOptions.map((option) => {
              const selected = option.selected === true;
              const kind = option.kind as SupplyAgentOverCapacityOption;
              const unavailable = option.status === 'NO_FEASIBLE_SOLUTION';
              const delta = numeric(option.delta_units) ?? 0;
              const fill = numeric(option.volume_fill_pct) ?? numeric(option.weight_fill_pct);
              return (
                <button
                  key={kind}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={switchingOption}
                  onClick={() => { if (!selected) onSelectOption?.(kind); }}
                  className={`rounded-md border px-3 py-2 text-left transition-colors disabled:cursor-wait disabled:opacity-60 ${selected ? 'border-emerald-500 bg-white ring-2 ring-emerald-200' : 'border-slate-200 bg-white/60 hover:border-slate-300 hover:bg-white'}`}
                >
                  <span className="block text-xs font-semibold text-slate-900">
                    {t(isLcl
                      ? (kind === 'reduce' ? 'supply.supplyAgent.fillToTargetOptionReduceLcl' : 'supply.supplyAgent.fillToTargetOptionCompleteLcl')
                      : (kind === 'reduce' ? 'supply.supplyAgent.fillToTargetOptionReduce' : 'supply.supplyAgent.fillToTargetOptionComplete'), { count: option.vehicles ?? 1 })}
                  </span>
                  {unavailable ? (
                    <span className="mt-1 block text-[11px] text-amber-800">{t('supply.supplyAgent.fillToTargetOptionUnavailable')}</span>
                  ) : (
                    <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] tabular-nums text-slate-600">
                      <span className={`rounded-full px-1.5 py-0.5 font-semibold ${delta < 0 ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {delta < 0 ? '▼ −' : '▲ +'}{formatNumber(Math.abs(delta), 0)} {t('supply.supplyAgent.fillToTargetOptionUnits')}
                      </span>
                      {fill != null ? <span>{t('supply.supplyAgent.fillToTargetOptionFill', { fill: formatNumber(fill, 0) })}</span> : null}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-4">
        <BeforeAfterBar label={t('supply.supplyAgent.loadMetricProducts')} before={volumeBefore} after={volumeAfter} target={target} />
        <BeforeAfterBar label={t('supply.supplyAgent.loadMetricWeight')} before={weightBefore} after={weightAfter} target={null} />
      </div>
      {notes.length > 0 ? (
        <ul role="status" className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-amber-900">
          {notes.map((note) => {
            const { head, reasons } = splitNoteReasons(note);
            return (
              <li key={note}>
                {head}
                {reasons.length > 0 ? (
                  <ul className="mt-0.5 list-disc space-y-0.5 pl-4">
                    {reasons.map((reason) => <li key={reason}>{reason}</li>)}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {limitItems.length > 0 ? (
        <p className="mt-2 text-[11px] text-slate-600">{t('supply.supplyAgent.fillToTargetLimitsApplied', { items: limitItems.join(' · ') })}</p>
      ) : null}
      {adjustments.length === 0 ? (
        <p className="mt-2 text-xs text-slate-700">{t('supply.supplyAgent.fillToTargetNoChanges')}</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-emerald-100 bg-white px-3 py-2 text-xs">
          <span className="tabular-nums text-slate-600">
            {t('supply.supplyAgent.fillToTargetUnitsSummary', { before: formatNumber(unitsBefore, 0), after: formatNumber(unitsAfter, 0) })}
          </span>
          <span
            className={`sv-pop rounded-full px-2 py-0.5 text-sm font-bold tabular-nums ${unitsDelta < 0 ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}
          >
            {unitsDelta < 0 ? '▼ −' : '▲ +'}{formatNumber(Math.abs(shownDelta ?? unitsDelta), 0)}
            {unitsBefore > 0 ? <span className="ml-1 text-[11px] font-semibold opacity-80">({unitsDelta < 0 ? '−' : '+'}{formatNumber(Math.abs(unitsDelta / unitsBefore) * 100, 1)} %)</span> : null}
          </span>
          {raisedCount > 0 ? <span className="text-[11px] font-medium text-emerald-700">▲ {t('supply.supplyAgent.fillToTargetRaised', { count: raisedCount })}</span> : null}
          {loweredCount > 0 ? <span className="text-[11px] font-medium text-rose-700">▼ {t('supply.supplyAgent.fillToTargetLowered', { count: loweredCount })}</span> : null}
          <span className="ml-auto flex items-center gap-1 text-[11px]">
            <button type="button" onClick={() => adjustments.forEach((adjustment) => onToggle(adjustment.product_id, true))} className="rounded px-1.5 py-0.5 font-medium text-emerald-800 hover:bg-emerald-50">
              {t('supply.supplyAgent.fillToTargetSelectAll')}
            </button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={() => adjustments.forEach((adjustment) => onToggle(adjustment.product_id, false))} className="rounded px-1.5 py-0.5 font-medium text-slate-600 hover:bg-slate-50">
              {t('supply.supplyAgent.fillToTargetSelectNone')}
            </button>
          </span>
          {lockedCount > 0 ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800">
              <Lock size={12} aria-hidden="true" />
              {t('supply.supplyAgent.fillToTargetLockedCount', { count: lockedCount })}
            </span>
          ) : null}
          <p className="basis-full text-[11px] text-slate-500">{t('supply.supplyAgent.fillToTargetReviewInTable')}</p>
        </div>
      )}
      {adjustments.length > 0 && summary ? (
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          <SummaryStat
            label={t('supply.supplyAgent.fillToTargetSummaryAmount')}
            value={amountBefore == null ? '—' : `${formatMoney(amountBefore)} → ${formatMoney(amountAfter)}`}
            delta={amountDelta != null ? signed(amountDelta, formatMoney) : null}
            note={unpricedNote}
          />
          <SummaryStat
            label={t('supply.supplyAgent.fillToTargetSummaryCoverage')}
            value={coverageBefore == null || coverageAfter == null
              ? '—'
              : `${t('supply.supplyAgent.fillToTargetSummaryDays', { days: formatNumber(coverageBefore, 0) })} → ${t('supply.supplyAgent.fillToTargetSummaryDays', { days: formatNumber(coverageAfter, 0) })}`}
            delta={coverageBefore != null && coverageAfter != null
              ? signed(coverageAfter - coverageBefore, (days) => t('supply.supplyAgent.fillToTargetSummaryDays', { days: formatNumber(days, 0) }))
              : null}
          />
          <SummaryStat
            label={t('supply.supplyAgent.fillToTargetSummaryOverstock')}
            value={overstockDelta != null
              ? signed(overstockDelta, formatMoney)
              : overstockUnitsDelta != null ? signed(overstockUnitsDelta, unitsLabel) : '—'}
            delta={overstockDelta != null && overstockUnitsDelta != null ? signed(overstockUnitsDelta, unitsLabel) : null}
            title={t('supply.supplyAgent.fillToTargetSummaryOverstockHelp')}
            tone={(overstockDelta ?? overstockUnitsDelta ?? 0) > 0 ? 'bad' : 'good'}
            note={overstockDelta == null ? unpricedNote : null}
          />
        </div>
      ) : null}
      {goal && adjustments.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-violet-100 bg-white px-3 py-2 text-xs">
          {goal.objective === 'pallet' ? (
            <>
              <span className="font-semibold text-slate-800">{t('supply.supplyAgent.fillToTargetGoalPallets')}</span>
              <span className="tabular-nums text-slate-700">{formatNumber(palletsBefore, 2)} → {formatNumber(palletsAfter, 2)} / {formatNumber(palletTarget, 2)}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${palletReached ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {palletReached ? t('supply.supplyAgent.fillToTargetGoalReached') : t('supply.supplyAgent.fillToTargetGoalMissing', { value: formatNumber(palletMissing, 2) })}
              </span>
            </>
          ) : (
            <>
              <span className="font-semibold text-slate-800">{t('supply.supplyAgent.fillToTargetGoalLayers')}</span>
              <span className="tabular-nums text-slate-700">{formatNumber(layersBefore, 0)} → {formatNumber(layersOpen, 0)}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${layersOpen === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                {layersOpen === 0 ? t('supply.supplyAgent.fillToTargetLayersDone') : t('supply.supplyAgent.fillToTargetLayersOpen', { count: layersOpen })}
              </span>
            </>
          )}
        </div>
      ) : null}
      {supplierRows.length > 1 && adjustments.length > 0 ? (
        <div className="mt-2">
          <SupplyVehicleSuppliersPanel rows={supplierRows} t={t} />
        </div>
      ) : null}
      {economic && economicTarget != null && adjustments.length > 0 ? (
        <div className="mt-2 rounded-md border border-sky-100 bg-white px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold text-slate-800">{t('supply.supplyAgent.fillToTargetAmountProgress')}</span>
            <span className="tabular-nums text-slate-700">{formatMoney(amountAfter)} / {formatMoney(economicTarget)}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${economicReached ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {economicReached
                ? t('supply.supplyAgent.fillToTargetAmountReached')
                : t('supply.supplyAgent.fillToTargetAmountMissing', { amount: formatMoney(economicMissing) })}
            </span>
          </div>
          {economicSaving != null ? (
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <SummaryStat
                label={t('supply.supplyAgent.fillToTargetEconomicSaving')}
                value={appliedSaving != null ? signed(appliedSaving, formatMoney) : '—'}
                note={appliedSaving == null ? t('supply.supplyAgent.fillToTargetEconomicNotReached') : null}
              />
              <SummaryStat
                label={t('supply.supplyAgent.fillToTargetEconomicOverstock')}
                value={overstockDelta != null ? signed(overstockDelta, formatMoney) : overstockUnitsDelta != null ? signed(overstockUnitsDelta, unitsLabel) : '—'}
                title={t('supply.supplyAgent.fillToTargetSummaryOverstockHelp')}
                note={overstockDelta == null ? unpricedNote : null}
              />
              <SummaryStat
                label={t('supply.supplyAgent.fillToTargetEconomicNet')}
                value={economicNet != null ? signed(economicNet, formatMoney) : '—'}
                tone={economicNet == null ? 'neutral' : economicNet >= 0 ? 'good' : 'bad'}
                delta={economicNet == null ? null : t(economicNet >= 0 ? 'supply.supplyAgent.fillToTargetEconomicWorth' : 'supply.supplyAgent.fillToTargetEconomicNotWorth')}
              />
            </div>
          ) : (
            <p className="mt-1.5 text-[11px] text-slate-500">{t('supply.supplyAgent.fillToTargetEconomicHint')}</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
